<template>
  <div class="preparing-for-position-badge">
    <Icon name="preparing_for_icon" size="small" class="preparing-icon" />
    <div
      v-if="preparing_for && preparing_for.length > 0"
      ref="avatarsRef"
      class="avatars-container"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <PeopleAvatarsButton
        :avatars="mappedAvatars"
        size="small"
        :maxVisible="3"
        :isHoverEnable="false"
        :isShowTooltip="false"
      />
    </div>
    <div v-else class="not-assigned">
      <div class="dot-container">
        <div class="dot"></div>
      </div>
      <span class="not-assigned-text">Not Assigned</span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onBeforeUnmount, defineProps } from "vue";
import Icon from "@components/icons/Icon.vue";
import PeopleAvatarsButton from "@components/PeopleAvatarsButton/PeopleAvatarsButton.vue";

const props = defineProps({
  preparing_for: {
    type: Array,
    default: () => null,
  },
});

const avatarsRef = ref(null);

/** Resolve the full name of one employee entry from any API shape. */
const fullNameOf = (emp) => {
  if (!emp) return "";
  const first = emp.first_name || emp.firstName || "";
  const last = emp.last_name || emp.lastName || "";
  const fromParts = `${first} ${last}`.trim();
  return (
    emp.assigned_employee_name ||
    emp.name ||
    emp.full_name ||
    emp.employee_name ||
    fromParts ||
    emp.email ||
    ""
  );
};

const mappedAvatars = computed(() => {
  if (!props.preparing_for) return [];
  return props.preparing_for.map((emp) => {
    const name = fullNameOf(emp);
    const nameParts = name.split(" ").filter(Boolean);
    const first = emp.first_name || emp.firstName || nameParts[0] || "";
    const last =
      emp.last_name ||
      emp.lastName ||
      (nameParts.length > 1 ? nameParts[nameParts.length - 1] : "") ||
      "";
    return {
      img:
        emp.assigned_employee_avatar ||
        emp.avatar ||
        emp.picture ||
        emp.photo ||
        emp.photoUrl ||
        "",
      firstName: first,
      lastName: last,
      _name: name,
    };
  });
});

/** Concatenated full names of all employees this position is preparing — used
 *  as the tooltip body, e.g. "Preparing: Jane Doe, John Smith". Falls back to
 *  a non-empty label so the tooltip still appears even when names are missing. */
const tooltipText = computed(() => {
  if (mappedAvatars.value.length === 0) return "";
  const names = mappedAvatars.value
    .map((a) => (a._name || `${a.firstName} ${a.lastName}`).trim())
    .filter(Boolean);
  if (names.length === 0) {
    return mappedAvatars.value.length === 1
      ? "Preparing"
      : `Preparing (${mappedAvatars.value.length})`;
  }
  return `Preparing: ${names.join(", ")}`;
});

let tooltipEl = null;

const ensureTooltip = () => {
  if (tooltipEl) return tooltipEl;
  tooltipEl = document.createElement("div");
  tooltipEl.className = "preparing-badge-cursor-tooltip";
  tooltipEl.innerHTML =
    '<span class="preparing-badge-cursor-tooltip__text"></span>';
  tooltipEl.style.display = "none";
  document.body.appendChild(tooltipEl);
  return tooltipEl;
};

const onMouseEnter = () => {
  const target = avatarsRef.value;
  if (!target) return;
  // tooltipText falls back to "Preparing" / "Preparing (N)" when individual
  // names are missing, so we still surface a tooltip on hover.
  if (!tooltipText.value) return;
  const rect = target.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const bottomY = rect.bottom + 8;
  const el = ensureTooltip();
  el.querySelector(".preparing-badge-cursor-tooltip__text").textContent =
    tooltipText.value;
  el.style.display = "";
  el.style.left = `${centerX}px`;
  el.style.top = `${bottomY}px`;
  el.style.transform = "translateX(-50%)";
};

const onMouseLeave = () => {
  if (tooltipEl) tooltipEl.style.display = "none";
};

onBeforeUnmount(() => {
  if (tooltipEl?.parentNode) {
    tooltipEl.parentNode.removeChild(tooltipEl);
    tooltipEl = null;
  }
});
</script>

<style scoped>
.preparing-for-position-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 5px;
  background: #FFFFFF;
  width: fit-content;
  height: 32px;
  box-sizing: border-box;
  position: relative;
}

.preparing-icon {
  flex-shrink: 0;
}

.avatars-container {
  display: inline-flex;
  align-items: center;
  width: fit-content;
}

.not-assigned {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px 2px 6px;
  background: #F2F4F7;
  border-radius: 6px;
  border: 1px solid #EAECF0;
  height: 22px;
  box-sizing: border-box;
}

.dot-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  height: 8px;
}

.dot {
  width: 6px;
  height: 6px;
  background: #667085;
  border-radius: 50%;
}

.not-assigned-text {
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #344054;
  white-space: nowrap;
}

/* Deep override for PeopleAvatarsButton to match small size and spacing */
:deep(.people-button),
:deep(.no-hover-people-button) {
  left: 0;
  padding: 0;
  display: inline-flex !important;
  width: fit-content !important;
}

:deep(._parent-content) {
  display: inline-flex !important;
  width: fit-content !important;
}

:deep(.el-tooltip) {
  display: inline-flex !important;
  width: fit-content !important;
}

:deep(.people-img-up-0),
:deep(.people-img-up-1),
:deep(.people-img-up-2),
:deep(.people-extra-people-1) {
  border-width: 2px !important;
  margin-left: -8px !important;
}

:deep(.people-img-up-0) {
  margin-left: 0 !important;
}

:deep(.people-extra-people-1-small) {
  width: 27px !important;
  height: 27px !important;
  font-size: 10px !important;
  background-color: #F2EDFF !important;
  color: #7F56D9 !important;
}

:deep(.people-img-up-0:hover),
:deep(.people-img-up-1:hover),
:deep(.people-img-up-2:hover),
:deep(.people-extra-people-1:hover) {
  transform: none !important;
}
</style>

<style>
.preparing-badge-cursor-tooltip {
  position: fixed;
  z-index: 99999;
  background-color: #0c111d;
  border-radius: 8px;
  padding: 8px 12px;
  pointer-events: none;
  white-space: pre-line;
  max-width: 320px;
  box-shadow:
    0px 4px 6px -2px rgba(16, 24, 40, 0.03),
    0px 12px 16px -4px rgba(16, 24, 40, 0.08);
}

.preparing-badge-cursor-tooltip__text {
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #ffffff;
}
</style>
